/* src/views/logements/FicheSiteCtrl.jsx — L'ONGLET « Site » de la fiche
   logement (lot 4, 2026-08-28) : quatre sections du proto (Le contenu ·
   Les photos · Les équipements · L'obligatoire), la carte d'état de la
   page, la colonne « Ce que voit le voyageur », et les actions.

   ON NE PERD AUCUNE FONCTION : la machine d'état est PORTÉE FIDÈLEMENT de
   LogementWebsiteTab (load logement_site_content, save site_content_upsert
   au payload identique, 4 gardes de publication, erreurs serveur traduites,
   flags de retouche manuelle EN/ES + « revenir à l'auto », import Beds24
   avec la modale de diff existante, équipements cfg + copier vers les
   chambres). La galerie photos = LogementGalleryEditor monté tel quel
   (upload compressé, cover, suppression). Les boutons IA n'existent plus
   (retirés R5) — les actions serveur orphelines sont supprimées dans ce
   même lot (décision 4). */

import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabase'
import { fetchListingFromBeds24 } from '../../lib/beds24-listings'
import { getBeds24Mapping } from '../../lib/beds24-mapping-lookup'
import ImportPreviewModal from '../../tabs/logements/ImportPreviewModal'
import LogementGalleryEditor from '../../tabs/logements/LogementGalleryEditor'
import CopyToChambresButton from '../../tabs/logements/CopyToChambresButton'
import { STANDARD_EQUIPEMENTS, EQUIPEMENTS_GROUPS, SLUG_REGEX,
  TRANSLATABLE_KEYS, callApi, FBZ_SITES_BASE_URL,
  slugifyName } from '../../tabs/logements/LogementWebsiteTab'
import { Segmentes } from './proto/InfosProto'

const noteStyle = { fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
  margin: '8px 0 0' }
const inpSite = { width: '100%', border: '1px solid #d5e3ee', borderRadius: '10px',
  padding: '10px', fontSize: '11.5px', color: 'var(--navy-d)', lineHeight: '1.6',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' }

/* ── la bascule des quatre sections ───────────────────────────────── */
function BasculeSite({ actif, onSec, pointLegal }) {
  const secs = [
    { id: 'contenu', lb: 'Le contenu' },
    { id: 'photos', lb: 'Les photos' },
    { id: 'equip', lb: 'Les équipements' },
    { id: 'legal', lb: "L'obligatoire", point: pointLegal },
  ]
  return (
    <div style={{ display: 'flex', gap: '3px', background: '#eef2f6',
      borderRadius: '10px', padding: '3px', marginBottom: '12px',
      overflowX: 'auto', scrollbarWidth: 'none', touchAction: 'pan-x' }}>
      {secs.map((sec) => (
        <button key={sec.id} onClick={() => onSec(sec.id)}
          data-testid={`site-sec-${sec.id}`}
          style={actif === sec.id
            ? { flex: '0 0 auto', display: 'inline-flex', alignItems: 'center',
              gap: '6px', minHeight: '38px', padding: '0 12px', borderRadius: '8px',
              fontSize: '11.5px', whiteSpace: 'nowrap', background: '#fff',
              color: 'var(--navy-d)', fontWeight: '600', border: 'none',
              boxShadow: '0 1px 3px rgba(35,50,64,.12)', cursor: 'pointer' }
            : { flex: '0 0 auto', display: 'inline-flex', alignItems: 'center',
              gap: '6px', minHeight: '38px', padding: '0 12px', borderRadius: '8px',
              fontSize: '11.5px', whiteSpace: 'nowrap', color: 'var(--gris)',
              background: 'transparent', border: 'none', cursor: 'pointer' }}>
          {sec.lb}{sec.point && (
            <span style={{ width: '6px', height: '6px', borderRadius: '50%',
              background: '#b8412b', flexShrink: '0' }} />
          )}
        </button>
      ))}
    </div>
  )
}

/* ── un champ traduit EN/ES avec badge auto/retouché + revert ─────── */
function ChampTraduit({ label, colKey, value, onEdit, onRevert, manuel, lignes = 2 }) {
  return (
    <div style={{ padding: '8px 0', borderTop: '1px solid var(--bord)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '6px' }}>
        <span style={{ flex: '1', fontSize: '11px', color: 'var(--gris)' }}>{label}</span>
        <span data-testid={`badge-${colKey}`}
          className={`tag ${manuel ? '' : 't-b'}`}
          style={manuel ? { background: 'var(--ambre-bg)', color: 'var(--ambre-tx)' } : {}}>
          {manuel ? '✎ retouché' : '⟳ auto'}</span>
        {manuel && (
          <button type="button" onClick={onRevert} data-testid={`revert-${colKey}`}
            title="Annule la retouche : ce champ redeviendra une traduction automatique du FR au prochain enregistrement."
            style={{ border: 'none', background: 'transparent', color: 'var(--navy)',
              cursor: 'pointer', fontSize: '10px', fontWeight: 600, padding: 0,
              textDecoration: 'underline' }}>revenir à l'auto</button>
        )}
      </div>
      <textarea value={value} rows={lignes}
        onChange={(e) => onEdit(e.target.value)}
        placeholder="vide = traduit tout seul depuis le français"
        style={{ ...inpSite, resize: 'vertical' }} />
    </div>
  )
}

export default function FicheSiteCtrl({ draft }) {
  const { selected } = draft
  const log = selected.entity
  const sisters = useMemo(() => {
    if (!selected.isChambre || !selected.parentLog) return []
    return (selected.parentLog.chambres || []).filter((ch) => ch.id !== log.id)
  }, [selected, log.id])

  const [section, setSection] = useState('contenu')
  const [avance, setAvance] = useState(false) // volet EN/ES du contenu

  // ── la machine d'état portée de LogementWebsiteTab ────────────────
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [slug, setSlug] = useState('')
  const [statutPublication, setStatutPublication] = useState('draft')
  const [annonceAirbnbUrl, setAnnonceAirbnbUrl] = useState('')
  const [annonceBookingUrl, setAnnonceBookingUrl] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [descriptionEs, setDescriptionEs] = useState('')
  const [metaSeoTitleFr, setMetaSeoTitleFr] = useState('')
  const [metaSeoDescFr, setMetaSeoDescFr] = useState('')
  const [metaSeoTitleEn, setMetaSeoTitleEn] = useState('')
  const [metaSeoDescEn, setMetaSeoDescEn] = useState('')
  const [metaSeoTitleEs, setMetaSeoTitleEs] = useState('')
  const [metaSeoDescEs, setMetaSeoDescEs] = useState('')
  const [manualFlags, setManualFlags] = useState({})
  const [politiqueAnnulation, setPolitiqueAnnulation] = useState('non_remboursable')
  const [numeroEnregistrement, setNumeroEnregistrement] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(null)

  useEffect(() => {
    let cancelled = false
    setSection('contenu')
    ;(async () => {
      setLoading(true); setLoadError(null); setSaveError(null); setSaveSuccess(null)
      try {
        const { data, error } = await supabase
          .from('logement_site_content').select('*')
          .eq('logement_id', log.id).maybeSingle()
        if (cancelled) return
        if (error) { setLoadError(error.message || 'Erreur chargement'); setLoading(false); return }
        if (data) {
          setSlug(data.slug || '')
          setStatutPublication(data.statut_publication || 'draft')
          setAnnonceAirbnbUrl(data.annonce_airbnb_url || '')
          setAnnonceBookingUrl(data.booking_url || '')
          setDescriptionFr(data.description_fr || '')
          setDescriptionEn(data.description_en || '')
          setDescriptionEs(data.description_es || '')
          setMetaSeoTitleFr(data.meta_seo_title_fr || '')
          setMetaSeoDescFr(data.meta_seo_description_fr || '')
          setMetaSeoTitleEn(data.meta_seo_title_en || '')
          setMetaSeoDescEn(data.meta_seo_description_en || '')
          setMetaSeoTitleEs(data.meta_seo_title_es || '')
          setMetaSeoDescEs(data.meta_seo_description_es || '')
          const hashes = (data.auto_translation_hashes && typeof data.auto_translation_hashes === 'object')
            ? data.auto_translation_hashes : {}
          const flags = {}
          for (const f of TRANSLATABLE_KEYS) {
            if (hashes[`manual_${f.key}_en`] === true) flags[f.en] = true
            if (hashes[`manual_${f.key}_es`] === true) flags[f.es] = true
          }
          setManualFlags(flags)
          setPolitiqueAnnulation(data.politique_annulation || 'non_remboursable')
          setNumeroEnregistrement(data.numero_enregistrement || '')
        } else {
          setSlug(slugifyName(log.nom_complet || log.nom || ''))
          setStatutPublication('draft')
          setAnnonceAirbnbUrl(''); setAnnonceBookingUrl('')
          setDescriptionFr(''); setDescriptionEn(''); setDescriptionEs('')
          setMetaSeoTitleFr(''); setMetaSeoDescFr('')
          setMetaSeoTitleEn(''); setMetaSeoDescEn('')
          setMetaSeoTitleEs(''); setMetaSeoDescEs('')
          setManualFlags({})
          setPolitiqueAnnulation('non_remboursable')
          setNumeroEnregistrement('')
        }
        setLoading(false)
      } catch (e) {
        if (!cancelled) { setLoadError(e?.message || 'Erreur réseau'); setLoading(false) }
      }
    })()
    return () => { cancelled = true }
  }, [log.id, log.nom, log.nom_complet])

  const editTraduit = (colKey, setter) => (value) => {
    setter(value)
    setManualFlags((prev) => (prev[colKey] === true ? prev : { ...prev, [colKey]: true }))
  }
  const revertTraduit = (colKey, setter) => () => {
    setter('')
    setManualFlags((prev) => ({ ...prev, [colKey]: false }))
  }

  const publishValidationErrors = useMemo(() => {
    const errors = []
    if (!slug || !SLUG_REGEX.test(slug)) errors.push('Slug invalide (a-z, 0-9, tirets)')
    if (!descriptionFr || descriptionFr.trim().length < 50) errors.push('Description FR < 50 caractères')
    if (!metaSeoTitleFr || metaSeoTitleFr.trim().length === 0) errors.push('Titre SEO FR requis')
    if (!politiqueAnnulation) errors.push('Politique annulation requise')
    return errors
  }, [slug, descriptionFr, metaSeoTitleFr, politiqueAnnulation])

  async function enregistrer(publish = false, statutForce = null) {
    setSaveError(null); setSaveSuccess(null)
    if (!slug || !SLUG_REGEX.test(slug)) {
      setSaveError('Slug invalide. Caractères autorisés : a-z, 0-9, tiret. Min 2, max 80.')
      return
    }
    if (publish && publishValidationErrors.length > 0) {
      setSaveError(`Publication impossible — corriger d'abord : ${publishValidationErrors.join(' · ')}`)
      return
    }
    setSaving(true)
    try {
      const statut = publish ? 'published' : (statutForce || statutPublication)
      const { ok, status, json } = await callApi('/api/sites?action=site_content_upsert', {
        logement_id: log.id,
        slug,
        statut_publication: statut,
        annonce_airbnb_url: annonceAirbnbUrl || null,
        annonce_booking_url: annonceBookingUrl || null,
        description_fr: descriptionFr,
        description_en: descriptionEn,
        meta_seo_title_fr: metaSeoTitleFr,
        meta_seo_description_fr: metaSeoDescFr,
        meta_seo_title_en: metaSeoTitleEn,
        meta_seo_description_en: metaSeoDescEn,
        description_es: descriptionEs,
        meta_seo_title_es: metaSeoTitleEs,
        meta_seo_description_es: metaSeoDescEs,
        manual_flags: manualFlags,
        politique_annulation: politiqueAnnulation,
        numero_enregistrement: numeroEnregistrement || null,
      })
      if (!ok) {
        const msg = json?.error === 'slug_conflict'
          ? `Slug déjà utilisé par un autre logement (${json.taken_by || 'inconnu'}).`
          : json?.error === 'invalid_slug' ? 'Slug invalide.'
            : json?.error === 'invalid_statut_publication' ? 'Statut publication invalide.'
              : json?.error === 'invalid_politique_annulation'
                ? 'Politique annulation invalide (flexible/moderee/stricte/non_remboursable).'
                : json?.error === 'invalid_numero_enregistrement_too_long'
                  ? "Numéro d'enregistrement trop long (max 100 caractères)."
                  : `Erreur ${status} : ${json?.error || 'inconnue'}`
        setSaveError(msg); setSaving(false)
        return
      }
      setStatutPublication(json.statut_publication || statut)
      setSaveSuccess(publish ? 'published' : 'saved')
      setSaving(false)
    } catch (e) {
      setSaveError(e?.message || 'Erreur inattendue'); setSaving(false)
    }
  }

  // ── import Beds24 (modale de diff existante) ──────────────────────
  const [importStatus, setImportStatus] = useState({ state: 'idle', error: null })
  const [previewListing, setPreviewListing] = useState(null)
  const [previewRaw, setPreviewRaw] = useState(null)
  const [mapping, setMapping] = useState(null)
  const [mappingLoading, setMappingLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    setMappingLoading(true)
    getBeds24Mapping(log.id)
      .then((row) => { if (!cancelled) { setMapping(row); setMappingLoading(false) } })
      .catch(() => { if (!cancelled) { setMapping(null); setMappingLoading(false) } })
    return () => { cancelled = true }
  }, [log.id])

  async function recupererBeds24() {
    if (!mapping) return
    setImportStatus({ state: 'loading', error: null })
    try {
      const result = await fetchListingFromBeds24(
        mapping.beds24_property_id, mapping.beds24_room_id)
      setPreviewListing(result.listing)
      setPreviewRaw(result.raw)
      setImportStatus({ state: 'idle', error: null })
    } catch (e) {
      setImportStatus({ state: 'error', error: e.message || String(e) })
    }
  }

  function appliquerImport(patch, fields) {
    const { description, ...cfgPatch } = patch || {}
    if (typeof description === 'string' && description.trim().length > 0) {
      setDescriptionFr(description)
    }
    if (Object.keys(cfgPatch).length > 0) {
      draft.updateLogBatch({
        ...cfgPatch,
        beds24_meta: {
          ...(log.beds24_meta || {}),
          last_imported_at: new Date().toISOString(),
          imported_fields: fields,
        },
      })
    }
    setPreviewListing(null); setPreviewRaw(null)
    setImportStatus({ state: 'success', error: null })
    setTimeout(() => setImportStatus((s) => (s.state === 'success'
      ? { state: 'idle', error: null } : s)), 6000)
  }

  // ── équipements (cfg, jamais site_content) ────────────────────────
  const equipements = Array.isArray(log.equipements) ? log.equipements : []
  const basculerEquipement = (id) => {
    const next = equipements.includes(id)
      ? equipements.filter((x) => x !== id)
      : [...equipements, id]
    draft.updateLog('equipements', next)
  }

  const publie = statutPublication === 'published'
  const peutVoir = publie && Boolean(slug)
  const urlPage = `${FBZ_SITES_BASE_URL}/fr/${slug}`

  if (loading) {
    return <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Chargement…</p>
  }
  if (loadError) {
    return (
      <div className="info" style={{ borderColor: '#e8c4b8', background: '#fbeee9',
        color: '#8a3b28' }}>Erreur chargement : {loadError}</div>
    )
  }

  const actions = (
    <div className="act" style={{ marginTop: '12px' }}>
      <button className="bt bt-s" data-testid="site-enregistrer" disabled={saving}
        onClick={() => enregistrer(false)}>
        {saving ? 'Enregistrement…' : 'Enregistrer le brouillon'}</button>
      {!publie ? (
        <button className="bt bt-p" data-testid="site-publier"
          disabled={saving || publishValidationErrors.length > 0}
          title={publishValidationErrors.length > 0
            ? `Publication bloquée : ${publishValidationErrors.join(' · ')}` : 'Publier la page'}
          style={publishValidationErrors.length > 0 ? { opacity: 0.55 } : {}}
          onClick={() => enregistrer(true)}>Publier</button>
      ) : (
        <button className="bt bt-s" data-testid="site-depublier" disabled={saving}
          onClick={() => enregistrer(false, 'draft')}>Dépublier</button>
      )}
    </div>
  )

  return (
    <>
      <div style={{ display: 'flex', gap: '9px', background: '#eef2f6',
        border: '1px dashed #c3cdd6', borderRadius: '12px', padding: '10px 11px',
        marginBottom: '10px' }}>
        <span style={{ width: '22px', height: '22px', borderRadius: '6px',
          background: '#fff', border: '1px solid var(--bord)', color: 'var(--gris)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', flexShrink: '0' }}>↻</span>
        <span style={{ flex: '1', minWidth: '0', fontSize: '10.5px',
          color: 'var(--gris-d)', lineHeight: '1.5' }}>La page publique reste{' '}
          <b>telle qu'elle est aujourd'hui</b>. C'est l'écran d'administration
          qui passe à notre charte.</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px',
        background: '#fff', borderRadius: '14px', padding: '11px 13px',
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}
        data-testid="site-etat">
        <span style={{ width: '34px', height: '34px', borderRadius: '10px',
          flexShrink: '0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '15px',
          background: publie ? 'var(--vert-bg)' : 'var(--ambre-bg)',
          color: publie ? 'var(--vert-tx)' : 'var(--ambre-tx)' }}>◈</span>
        <span style={{ flex: '1', minWidth: '0' }}>
          <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
            color: 'var(--navy-d)' }}>
            {publie ? 'Page publiée' : (statutPublication === 'archived' ? 'Page archivée' : 'Page en brouillon')}</span>
          <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
            marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' }}>
            {slug ? urlPage.replace('https://', '') : 'adresse à poser (L’obligatoire)'}</span>
        </span>
        <button onClick={() => { if (peutVoir) window.open(urlPage, '_blank', 'noopener') }}
          data-testid="site-voir" disabled={!peutVoir}
          title={peutVoir ? `Ouvrir ${urlPage}` : "Publiez d'abord (statut Publié + adresse)"}
          style={{ minHeight: '34px', padding: '0 11px', borderRadius: '8px',
            border: '1px solid #c3d5e4', background: '#fff', fontSize: '11px',
            color: 'var(--navy)', flexShrink: '0',
            cursor: peutVoir ? 'pointer' : 'not-allowed',
            opacity: peutVoir ? 1 : 0.55 }}>Voir</button>
      </div>

      {saveError && (
        <div className="info" data-testid="site-save-erreur"
          style={{ borderColor: '#e8c4b8', background: '#fbeee9',
            color: '#8a3b28' }}>{saveError}</div>
      )}
      {saveSuccess && (
        <div className="info" data-testid="site-save-succes"
          style={{ borderColor: 'var(--vert-bd)', background: 'var(--vert-bg)',
            color: 'var(--vert-tx)' }}>
          {saveSuccess === 'published' ? 'Publié — la page se met à jour.' : 'Brouillon enregistré.'}</div>
      )}

      <div className="cols2">
        <div>
          <BasculeSite actif={section} onSec={setSection}
            pointLegal={!numeroEnregistrement || publishValidationErrors.length > 0} />

          {/* ═══ Le contenu ═══════════════════════════════════════ */}
          {section === 'contenu' && (
            <>
              <div className="carte" style={{ margin: '0 0 9px' }}>
                <span style={{ display: 'block', fontSize: '11.5px',
                  color: 'var(--gris)', marginBottom: '6px' }}>La description</span>
                <textarea value={descriptionFr} rows={5}
                  data-testid="site-description-fr"
                  onChange={(e) => setDescriptionFr(e.target.value)}
                  placeholder="Une description honnête du logement — 50 caractères minimum pour publier."
                  style={{ ...inpSite, resize: 'vertical', minHeight: '80px' }} />
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px',
                  flexWrap: 'wrap' }}>
                  <span className="tag t-v">français · source</span>
                  <span className="tag t-b">anglais · {manualFlags.description_en ? 'retouché' : 'traduit'}</span>
                  <span className="tag t-b">espagnol · {manualFlags.description_es ? 'retouché' : 'traduit'}</span>
                </div>
                <p className="note">Une retouche en anglais ou en espagnol n'est
                  jamais écrasée, jusqu'à ce que vous reveniez à l'automatique.</p>
              </div>
              <p className="inter">Pour être trouvé sur les moteurs</p>
              <div className="carte" style={{ margin: '0' }}>
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                    marginBottom: '6px' }}>
                    <span style={{ flex: '1', fontSize: '12px',
                      color: 'var(--gris)' }}>Titre</span>
                    <span style={{ fontSize: '10.5px',
                      color: (metaSeoTitleFr || '').length > 60 ? '#b8412b' : 'var(--gris-d)' }}>
                      {(metaSeoTitleFr || '').length} / 60</span>
                  </div>
                  <input value={metaSeoTitleFr} data-testid="site-seo-titre"
                    onChange={(e) => setMetaSeoTitleFr(e.target.value)}
                    style={{ ...inpSite, minHeight: '38px' }} />
                </div>
                <div style={{ padding: '8px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                    marginBottom: '6px' }}>
                    <span style={{ flex: '1', fontSize: '12px',
                      color: 'var(--gris)' }}>Description</span>
                    <span style={{ fontSize: '10.5px',
                      color: (metaSeoDescFr || '').length > 160 ? '#b8412b' : 'var(--gris-d)' }}>
                      {(metaSeoDescFr || '').length} / 160</span>
                  </div>
                  <input value={metaSeoDescFr} data-testid="site-seo-desc"
                    onChange={(e) => setMetaSeoDescFr(e.target.value)}
                    style={{ ...inpSite, minHeight: '38px' }} />
                </div>
                <p className="note">C'est ce qui apparaît dans les résultats de
                  recherche, sous le lien.</p>
              </div>
              {/* Les retouches EN/ES + « revenir à l'auto » (fonctions
                  conservées, repliées derrière un volet). */}
              <button className="bt bt-s" style={{ marginTop: '9px' }}
                data-testid="site-volet-langues"
                onClick={() => setAvance((v) => !v)}>
                {avance ? 'Masquer' : 'Retoucher'} l'anglais et l'espagnol</button>
              {avance && (
                <div className="carte" style={{ margin: '9px 0 0' }}>
                  <ChampTraduit label="Description — anglais" colKey="description_en"
                    value={descriptionEn} manuel={manualFlags.description_en === true}
                    onEdit={editTraduit('description_en', setDescriptionEn)}
                    onRevert={revertTraduit('description_en', setDescriptionEn)}
                    lignes={3} />
                  <ChampTraduit label="Titre moteur — anglais" colKey="meta_seo_title_en"
                    value={metaSeoTitleEn} manuel={manualFlags.meta_seo_title_en === true}
                    onEdit={editTraduit('meta_seo_title_en', setMetaSeoTitleEn)}
                    onRevert={revertTraduit('meta_seo_title_en', setMetaSeoTitleEn)}
                    lignes={1} />
                  <ChampTraduit label="Description moteur — anglais" colKey="meta_seo_description_en"
                    value={metaSeoDescEn} manuel={manualFlags.meta_seo_description_en === true}
                    onEdit={editTraduit('meta_seo_description_en', setMetaSeoDescEn)}
                    onRevert={revertTraduit('meta_seo_description_en', setMetaSeoDescEn)}
                    lignes={1} />
                  <ChampTraduit label="Description — espagnol" colKey="description_es"
                    value={descriptionEs} manuel={manualFlags.description_es === true}
                    onEdit={editTraduit('description_es', setDescriptionEs)}
                    onRevert={revertTraduit('description_es', setDescriptionEs)}
                    lignes={3} />
                  <ChampTraduit label="Titre moteur — espagnol" colKey="meta_seo_title_es"
                    value={metaSeoTitleEs} manuel={manualFlags.meta_seo_title_es === true}
                    onEdit={editTraduit('meta_seo_title_es', setMetaSeoTitleEs)}
                    onRevert={revertTraduit('meta_seo_title_es', setMetaSeoTitleEs)}
                    lignes={1} />
                  <ChampTraduit label="Description moteur — espagnol" colKey="meta_seo_description_es"
                    value={metaSeoDescEs} manuel={manualFlags.meta_seo_description_es === true}
                    onEdit={editTraduit('meta_seo_description_es', setMetaSeoDescEs)}
                    onRevert={revertTraduit('meta_seo_description_es', setMetaSeoDescEs)}
                    lignes={1} />
                  <p className="note">Le français est l'unique source : l'anglais
                    et l'espagnol se traduisent tout seuls à l'enregistrement,
                    sauf retouche.</p>
                </div>
              )}
              <p className="inter">Récupérer depuis la plateforme</p>
              <div className="carte" style={{ margin: '0' }}>
                <p style={{ fontSize: '11.5px', margin: '0', color: 'var(--gris)',
                  lineHeight: '1.6' }}>Beds24 vous montre{' '}
                  <b style={{ color: 'var(--navy-d)' }}>les différences avant
                  d'appliquer</b>. Rien ne repart jamais dans l'autre sens.</p>
                {importStatus.state === 'error' && (
                  <div className="info" style={{ borderColor: '#e8c4b8',
                    background: '#fbeee9', color: '#8a3b28',
                    marginTop: '8px' }}>{importStatus.error}</div>
                )}
                {importStatus.state === 'success' && (
                  <div className="info" style={{ borderColor: 'var(--vert-bd)',
                    background: 'var(--vert-bg)', color: 'var(--vert-tx)',
                    marginTop: '8px' }}>Importé.</div>
                )}
                {!mappingLoading && !mapping && (
                  <p style={noteStyle}>Pas de raccordement Beds24 actif pour ce
                    logement — configurez-le d'abord (Infos → Le raccordement).</p>
                )}
                <div className="act">
                  <button className="bt bt-s" data-testid="site-recuperer"
                    disabled={mappingLoading || !mapping || importStatus.state === 'loading'}
                    onClick={recupererBeds24}>
                    {importStatus.state === 'loading' ? 'Import en cours…' : 'Récupérer'}</button>
                </div>
              </div>
              {actions}
            </>
          )}

          {/* ═══ Les photos ═══════════════════════════════════════ */}
          {section === 'photos' && (
            <>
              <div className="info">Elles servent au site public <b>et</b> au
                livret voyageur. La première fait la couverture.</div>
              {/* Galerie existante montée telle quelle : upload compressé +
                  rollback, étoile de couverture, suppression confirmée. */}
              <LogementGalleryEditor logement_id={log.id} />
              {actions}
            </>
          )}

          {/* ═══ Les équipements ══════════════════════════════════ */}
          {section === 'equip' && (
            <>
              <div className="info"><b>{equipements.length} coché{equipements.length > 1 ? 's' : ''}</b> sur{' '}
                {STANDARD_EQUIPEMENTS.length}. Ils s'affichent en pictogrammes
                sur la page.</div>
              {EQUIPEMENTS_GROUPS.map((groupe) => {
                const items = STANDARD_EQUIPEMENTS.filter((eq) => eq.group === groupe)
                const nbCoches = items.filter((eq) => equipements.includes(eq.id)).length
                return (
                  <div className="carte" key={groupe} style={{ margin: '0 0 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '9px',
                      marginBottom: '8px' }}>
                      <span style={{ flex: '1', fontSize: '11px', fontWeight: '600',
                        color: 'var(--navy-d)', textTransform: 'uppercase',
                        letterSpacing: '.05em' }}>{groupe}</span>
                      <span style={{ fontSize: '10.5px',
                        color: 'var(--gris-d)' }}>{nbCoches} / {items.length}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {items.map((eq) => {
                        const coche = equipements.includes(eq.id)
                        const label = eq.label.replace(/^\S+\s/, '')
                        return (
                          <button key={eq.id} onClick={() => basculerEquipement(eq.id)}
                            data-testid={`site-equip-${eq.id}`}
                            aria-pressed={coche}
                            style={coche
                              ? { display: 'inline-flex', alignItems: 'center',
                                gap: '6px', minHeight: '32px', padding: '0 11px',
                                borderRadius: '9px', fontSize: '11px',
                                background: 'var(--ciel-p)', border: '1px solid #87b8e6',
                                color: 'var(--navy-d)', fontWeight: '600',
                                cursor: 'pointer' }
                              : { display: 'inline-flex', alignItems: 'center',
                                gap: '6px', minHeight: '32px', padding: '0 11px',
                                borderRadius: '9px', fontSize: '11px',
                                background: '#fff', border: '1px solid #d5e3ee',
                                color: 'var(--gris)', cursor: 'pointer' }}>
                            {coche && (
                              <span style={{ width: '14px', height: '14px',
                                borderRadius: '4px', background: '#87b8e6',
                                color: '#fff', display: 'inline-flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: '9px' }}>✓</span>
                            )}
                            {label}</button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
              <div className="act">
                <CopyToChambresButton sourceLog={log} sisters={sisters}
                  sectionId="equipements"
                  onCopyToChambres={draft.copyToChambres} />
              </div>
              <p className="note">Ils ne partent jamais vers Airbnb ni Booking :
                les équipements y restent gérés sur chaque plateforme. Cochés
                ici, ils s'enregistrent avec la pastille « Enregistrer » de la
                fiche (pas par le brouillon du site).</p>
              {actions}
            </>
          )}

          {/* ═══ L'obligatoire ════════════════════════════════════ */}
          {section === 'legal' && (
            <>
              {!numeroEnregistrement && (
                <div className="carte" style={{ margin: '0 0 9px',
                  borderColor: 'var(--ambre-bd)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '28px', height: '28px', borderRadius: '8px',
                      background: 'var(--ambre-bg)', color: 'var(--ambre-tx)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', flexShrink: 0 }}>!</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '12px',
                        fontWeight: '600', color: 'var(--navy-d)' }}>Numéro
                        d'enregistrement meublé</span>
                      <span style={{ display: 'block', fontSize: '10.5px',
                        color: 'var(--ambre-tx)', marginTop: '1px' }}>vide — la
                        mention légale n'apparaît pas sur la page</span>
                    </span>
                  </div>
                </div>
              )}
              <div className="carte" style={{ margin: '0 0 9px' }}>
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--gris)', marginBottom: '6px' }}>Numéro
                    d'enregistrement meublé (optionnel)</span>
                  <input value={numeroEnregistrement} maxLength={100}
                    data-testid="site-numero-enregistrement"
                    onChange={(e) => setNumeroEnregistrement(e.target.value)}
                    placeholder="ex : 31113000012AB"
                    style={{ ...inpSite, minHeight: '38px' }} />
                </div>
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--gris)', marginBottom: '7px' }}>Politique
                    d'annulation</span>
                  <select value={politiqueAnnulation}
                    data-testid="site-politique"
                    onChange={(e) => setPolitiqueAnnulation(e.target.value)}
                    style={{ ...inpSite, minHeight: '38px' }}>
                    <option value="non_remboursable">Non remboursable (défaut FBZ)</option>
                    <option value="stricte">Stricte (50 % si &gt; 30 j)</option>
                    <option value="moderee">Modérée (50 % si &gt; 5 j)</option>
                    <option value="flexible">Flexible (100 % si &gt; 48 h)</option>
                  </select>
                </div>
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--gris)', marginBottom: '6px' }}>Adresse de la
                    page <span style={{ fontSize: '10px',
                      color: 'var(--gris-d)' }}>lettres, chiffres et tirets</span></span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input value={slug} data-testid="site-slug"
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="cocon-naturel"
                      style={{ ...inpSite, minHeight: '38px', flex: 1 }} />
                    <button onClick={() => setSlug(slugifyName(log.nom_complet || log.nom || ''))}
                      title="Reprendre depuis le nom du logement"
                      aria-label="Régénérer l'adresse depuis le nom"
                      style={{ minHeight: '38px', padding: '0 11px', borderRadius: '9px',
                        border: '1px solid #c3d5e4', background: '#fff',
                        fontSize: '13px', color: 'var(--navy)', flexShrink: 0,
                        cursor: 'pointer' }}>↺</button>
                  </div>
                </div>
                <div style={{ padding: '8px 0' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--gris)', marginBottom: '7px' }}>État</span>
                  <Segmentes valeur={statutPublication}
                    testIdPrefix="site-statut"
                    options={[{ value: 'draft', label: 'Brouillon' },
                      { value: 'published', label: 'Publié' },
                      { value: 'archived', label: 'Archivé' }]}
                    onChoisir={(v) => setStatutPublication(v)} />
                </div>
                <p className="note">Le tarif « à partir de » se calcule tout seul
                  sur les soixante prochains jours. Rien à saisir.</p>
              </div>
              <p className="inter">Si le voyageur préfère la plateforme</p>
              <div className="carte" style={{ margin: '0' }}>
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--bord)' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--gris)', marginBottom: '6px' }}>Page Airbnb</span>
                  <input value={annonceAirbnbUrl} data-testid="site-url-airbnb"
                    onChange={(e) => setAnnonceAirbnbUrl(e.target.value)}
                    placeholder="https://airbnb.fr/rooms/…"
                    style={{ ...inpSite, minHeight: '38px' }} />
                </div>
                <div style={{ padding: '8px 0' }}>
                  <span style={{ display: 'block', fontSize: '12px',
                    color: 'var(--gris)', marginBottom: '6px' }}>Page Booking</span>
                  <input value={annonceBookingUrl} data-testid="site-url-booking"
                    onChange={(e) => setAnnonceBookingUrl(e.target.value)}
                    placeholder="https://booking.com/hotel/…"
                    style={{ ...inpSite, minHeight: '38px' }} />
                </div>
                <p className="note">Ces deux adresses servent de repli : la page
                  les propose si le direct n'aboutit pas.</p>
              </div>
              {publishValidationErrors.length > 0 && (
                <div className="warn" data-testid="site-gardes">Publication
                  bloquée — corriger d'abord : {publishValidationErrors.join(' · ')}.</div>
              )}
              {actions}
            </>
          )}
        </div>

        {/* ── colonne droite : la page en cinq sections ────────────── */}
        <div className="surOrdi">
          <div style={{ position: 'sticky', top: '14px' }}>
            <p className="inter" style={{ marginTop: '0' }}>Ce que voit le voyageur</p>
            <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
              <div style={{ height: '92px',
                background: 'linear-gradient(135deg,#c3d5e4,#87b8e6)',
                display: 'flex', alignItems: 'flex-end',
                justifyContent: 'space-between', padding: '11px 13px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600',
                  color: '#fff' }}>{log.nom_complet || log.nom || log.id}</span>
              </div>
              <div style={{ padding: '12px 13px' }}>
                <div style={{ background: '#f6f8fa', borderRadius: '11px',
                  padding: '11px', marginBottom: '11px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '600',
                    color: 'var(--navy-d)', margin: '0 0 7px' }}>Votre séjour</p>
                  {['Vos dates', 'Combien de voyageurs', 'Vos coordonnées'].map((lb) => (
                    <div key={lb} style={{ background: '#fff',
                      border: '1px solid #d5e3ee', borderRadius: '8px',
                      padding: '8px 10px', marginBottom: '5px', fontSize: '10.5px',
                      color: 'var(--gris-d)' }}>{lb}</div>
                  ))}
                  <div style={{ background: 'var(--terra)', borderRadius: '8px',
                    padding: '9px', textAlign: 'center', fontSize: '11px',
                    color: '#fff', fontWeight: '600', marginTop: '7px' }}>Envoyer ma demande</div>
                  <p style={{ fontSize: '9.5px', color: 'var(--gris)',
                    margin: '6px 0 0', textAlign: 'center' }}>Aucun paiement
                    maintenant · réponse sous quelques heures</p>
                </div>
                {['Ce qui vous engage', 'Le logement', "Ce qu'ils en disent",
                  "Ce qu'on nous demande", 'Le même logement, ailleurs'].map((lb, i) => (
                  <div key={lb} style={{ display: 'flex', alignItems: 'center',
                    gap: '9px', padding: '8px 0',
                    borderTop: i > 0 ? '1px solid var(--bord)' : 'none' }}>
                    <span style={{ fontSize: '10px', color: 'var(--gris-d)',
                      flexShrink: '0' }}>{String(i + 1).padStart(2, '0')}</span>
                    <span style={{ flex: '1', fontSize: '11px',
                      color: 'var(--navy-d)' }}>{lb}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="note">Les cinq sections de la page, dans leur ordre.
              Le prix et les avis viennent des données, pas d'une saisie.</p>
          </div>
        </div>
      </div>

      {previewListing && (
        <ImportPreviewModal
          log={log}
          listing={previewListing}
          rawData={previewRaw}
          onApply={appliquerImport}
          onClose={() => {
            setPreviewListing(null); setPreviewRaw(null)
            setImportStatus({ state: 'idle', error: null })
          }} />
      )}
    </>
  )
}
